import Phaser from 'phaser';
import { ARTIKEL_POOL_ID } from '@/data/pools';

/**
 * The little coloured shape that rides in front of the banner's key word.
 *
 * It exists for one lesson: "Der, Die, Das" (specs/artikel-lesson.md). German classrooms mark the
 * three genders with a shape and a colour long before the child can read the article itself —
 * **blue triangle der · red circle die · green square das** — and a child who has met those symbols
 * at school recognises the board's question from the shape alone, a beat before the word.
 *
 * Purely visual, and deliberately so: the marker is a drawn shape next to the text, never a
 * character inside it, so the prompt string handed to `speakRulePrompt` stays the plain sentence.
 * A symbol spliced into the text would be read out as "schwarzes Dreieck" or, worse, swallowed with
 * the word next to it.
 *
 * The colours are the ones the lesson's own reward star already uses (public/images/artikel-star.svg
 * — blau der, rot die, grün das), lightened for the near-black banner pill.
 */
export type MarkerShape = 'triangle' | 'circle' | 'square';

export interface PromptMarker {
  shape: MarkerShape;
  color: number;
}

const ARTICLE_MARKERS: Record<string, PromptMarker> = {
  der: { shape: 'triangle', color: 0x5aa9ff },
  die: { shape: 'circle', color: 0xff6b7d },
  das: { shape: 'square', color: 0x54cf85 },
};

/**
 * The marker for a board's rule, or null when the lesson has none — which is every lesson but this
 * one. Gated on the pool id rather than on the class id alone: "der"/"die"/"das" are three ordinary
 * strings, and a class that happens to be called one of them in some future pack should not
 * inherit a school symbol for genders it is not teaching.
 */
export function markerForRule(packId: string, classId: string): PromptMarker | null {
  if (packId !== ARTIKEL_POOL_ID) return null;
  return ARTICLE_MARKERS[classId] ?? null;
}

/** Marker height as a share of the banner's font size — roughly the cap height of the text. */
export const MARKER_SCALE = 0.72;

/**
 * All three markers advance the line by the same width, whatever shape they are. The banner is
 * centred on the canvas and the three prompts are otherwise the same sentence, so a per-shape width
 * would shift the whole question left and right as the board changes article — a wobble with no
 * meaning behind it.
 */
const ADVANCE = 1.12;

/**
 * Draws one marker centred on its own (0, 0), sized around `size`, ready to be dropped into the
 * banner's row. Graphics rather than a glyph: `▲ ● ■` are one font substitution away from tofu on
 * a device whose UI font has no geometric shapes, and the shapes have to carry the meaning here.
 */
export function drawPromptMarker(
  scene: Phaser.Scene,
  marker: PromptMarker,
  size: number,
): Phaser.GameObjects.Container {
  const g = scene.add.graphics();
  g.fillStyle(marker.color, 1);
  switch (marker.shape) {
    case 'square': {
      // A square reads heavier than a circle of the same span, so it is drawn a shade smaller —
      // the three are matched by eye, not by bounding box.
      const s = size * 0.90;
      g.fillRect(-s / 2, -s / 2, s, s);
      break;
    }
    case 'circle':
      g.fillCircle(0, 0, size * 0.5);
      break;
    case 'triangle': {
      const w = size * 1.12;
      const h = size * 0.97;
      g.fillTriangle(0, -h / 2, w / 2, h / 2, -w / 2, h / 2);
      break;
    }
  }

  const box = scene.add.container(0, 0, [g]);
  box.setSize(size * ADVANCE, size);
  return box;
}
